import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ordersService } from '../../services/orders.service';
import { MessageService } from 'primeng/api';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmsServicesService } from 'src/app/module/ftl-master/services/ems-services.service';
import { buildLrDocumentData, buildLrTemplateData, downloadLrHtml, printLrHtml, renderLrDocument, renderLrHtml } from './lr-template.util';
import * as XLSX from 'xlsx';

export type TripRole = 'ROLE_KAM' | 'ROLE_PROCUREMENT' | 'ROLE_PROCUREMENT_HEAD' | 'ROLE_VENDOR' | 'ROLE_OPERATION';

// ── Order (from getOrderById) ──
export interface TripOrderViewModel {
  id: number;
  orderNo: string;
  customerName: string;
  orderType: string;
  vehicleRequiredAt: string;
  assignedKam:any;
  assignedProcExec:any;
  route: string;
  source: string;
  destination: string;
  vehicleTypeId: number;
  numberOfVehicle: number;
  vendorId?: number;
  accountId?: number;
  customerId?: any;
  orderId: number
  status: string;
  confirmedAmount?: number;
  procurementProvidedAmount?: number;
  orderStatus?: any[];
}

// ── Trip (from getOrderTripById) ──
export interface TripDataModel {
  id?: number;
  orderId?: number;
  accountId?: number;
  customerId?: number;
  vendorId?: number;
  vendorName?: string;
  tripNumber?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverMobile?: string;
  vendorMobile?: any;
  licenseNumber?: string;
  sealNumber?: string;
  advanceAmount?: number;
  advancePercentage:any;
  reportingDateTime?: string;
  loadingDateTime?: string;
  materialTypeDescription?: string;
  weight?: number;
  numberOfPackages?: number;
  managementStatus?: string;
  lhcStatus?: string;
  status?: string;
  trackingId?: string;
  remarks?: string;
}

// ── One row in the repeatable LHC form (vehicle + payment only) ──
export interface LhcFormRow {
  id?: number;          // trip id once saved, undefined for a new unsaved row
  vehicleNumber: string;
  driverName: string;
  driverMobile: string;
  vendorMobile:any;
  vendorName:any;
  licenseNumber: string;
  advanceAmount: number | null;
  advancePercentage:any;
  saving?: boolean;
}

// ── One row in the repeatable LR — Lorry Receipt section ──
// Decoupled from LhcFormRow — count of LRs is independent of vehicle count.
// All rows are submitted together via a single section-level Save.
export interface LrFormRow {
  id?: number;
  isManual: boolean;        
  lrNumber: string
  lrDate: string;           
  remarks: string
  lrDocument?: string;      
  uploading?: boolean;
  lrNumberGenerating?: boolean;
}

// ── One row in the repeatable E-Way Bill section ──
// All rows are submitted together via a single section-level Save.
export interface EwayBillRow {
  id?: number;

  gstNumber: string;
  ewayBillNumber: string;
  fetching?: boolean;
  fetched?: boolean;
  uploading?: boolean;
  docUrl?: string;

  errorMessage?: string;

  // Display fields populated after Fetch
  ewbId?: number;
  invoiceNo?: string;
  invoiceValue?: number;
  vehicleNo?: string;
  lrNumber?: string;

  // Raw "DD-MM-YYYY HH:mm:ss +0000" strings exactly as returned by the
  // fetch API — passed straight through to the save payload unchanged.
  rawInvoiceDate?: string;
  rawLrDate?: string;
  rawEwbDate?: string;
  rawValidUpto?: string;

  // Parsed Date objects — used only for display formatting.
  invoiceDateObj?: Date | null;
  lrDateObj?: Date | null;
  ewbDateObj?: Date | null;
  validUptoObj?: Date | null;
}

// Pickup / Drop Location section ──
export interface LocationFormRow {
  id?: number;
  pincode: string;
  city: any;        
  otherCity: string;
  address: string;
  // Links this pickup/drop pair to one of the LR rows entered in the
  // "LR — Lorry Receipt" section above (1 LR per pickup+drop pair).
  // The select is live on the UI, but sending it in the save payload
  // is commented out below until the backend API supports it.
  lrId: any;
}

export interface EwayBulkRow {
  gstNumber: string;
  ewayBillNumber: string;
  status: 'pending' | 'fetching' | 'success' | 'error' | 'skipped';
  errorMessage?: string;
}

@Component({
  selector: 'app-trip-order',
  templateUrl: './trip-order.component.html',
  styleUrls: ['./trip-order.component.css']
})
export class TripOrderComponent implements OnInit {

  role: TripRole = 'ROLE_PROCUREMENT';
  roleNames: string[] = [];
  userName = '';
  orderId: any;

  order!: TripOrderViewModel;
  tripData: TripDataModel | null = null;

  isLoading = false;
  loadingSaving = false;
  lrSaving = false;
  ewaySaving = false;

  // Repeatable LHC rows — vehicle/driver/payment only
  lhcRows: LhcFormRow[] = [];

  // Repeatable LR — Lorry Receipt rows — independent of LHC rows.
  // Single Save button submits every row.
  lrRows: LrFormRow[] = [];

  // Repeatable E-Way Bill rows — single Save button submits every row.
  ewayRows: EwayBillRow[] = [];

  // GST numbers available for this account — populated for the
  // e-Way Bill "GST Number" dropdown.
  gstList: any[] = [];

  loadingForm = {
    reportingDateTime: '',
    loadingDateTime: '',
    sealNumber: '',
    materialTypeDescription: '',
    weight: null as number | null,
    numberOfPackages: null as number | null,
  };
  kamList:any[]=[]
  procurementExecList:any[]=[]
  allRoles:any[]=[]
  // Snapshot of the last saved/loaded loadingForm — used to detect
  // whether the user has made any unsaved changes, so the Save
  // button can disable itself just like the LR / E-Way Bill sections.
  private loadingFormSnapshot: string = JSON.stringify(this.loadingForm);

  // Repeatable Pickup / Drop rows — ROLE_OPERATION only, single Save.
  // Pickup & Drop are always added/removed together in pairs — 1 LR
  // is meant to map to 1 pickup + 1 drop pair (see LocationFormRow.lrNumber).
  pickupRows: LocationFormRow[] = [];
  dropRows: LocationFormRow[] = [];
  locationsSaving = false;
  locationList: any[] = [];

  ewayBillList:any;
  lrInfoList:any;
  vehicleTypeList:any;
  uploadApi     = '';

  trackingFetching = false;
  trackingData: { id?: number; url?: string; lrNumber?: string } | null = null;

  showEwayBulkModal = false;
  ewayBulkProcessing = false;
  ewayBulkRows: EwayBulkRow[] = [];
  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private orderService: ordersService,
    private emsServices: EmsServicesService,
    private messageService: MessageService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    this.roleNames = (userDetails?.userRoles || []).map((r: any) => r.name);
    this.role = (this.roleNames[0] as TripRole) || 'ROLE_PROCUREMENT';
    this.userName = userDetails?.name || '';

    this.orderId = this.activeRoute.snapshot.params['id'];
    if (this.orderId) {
      this.isLoading = true;
      this.getOrderDatabyId(this.orderId);
      this.getVehicleType();
      this.getLocations();
    }
  }

  hasRole(...roles: string[]): boolean {
    return roles.some(r => this.roleNames.includes(r));
  }
  notRole(...roles: string[]): boolean {
    return !this.hasRole(...roles);
  }

  // ─────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────

  // Load order first — its orderStatus tells us whether a trip
  // record already exists (TRIP_STATUS !== 'PENDING') or whether
  // we need to create one before loading trip details.
  getOrderDatabyId(orderId: any): void {
    this.orderService.getOrderById(orderId).subscribe({
      next: (res: any) => {
        const data = res?.responseObject;
        if (data) {
          this.getRoles();
          this.mapResponseToOrder(data);
          this.populateLocationRows(data.orderPoints);
          this.loadGstList();

          if (this.isTripPending()) {
            this.createTrip();
          } else {
            this.getOrderTripById(this.orderId);
          }
        } else {
          this.isLoading = false;
        }
      },
      error: () => { this.isLoading = false; }
    });
  }

  getLocations(): void {
    this.emsServices.getLocations([], 0, 100000).subscribe((res: any) => {
      this.locationList = res?.responseObject?.content || [];
      this.locationList.unshift({ id: -1, cityName: 'Others' });
    });
  }

  isTripPending(): boolean {
    return this.order?.orderStatus?.some(
      (el: any) => el.statusType === 'TRIP_STATUS' && el.status === 'PENDING'
    ) || false;
  }

  // First-time only: create the trip shell record, then load
  // its details the normal way.
  createTrip(): void {
    const payload: any = {
      orderId:    this.order.id,
      accountId:  this.order.accountId || 1,
      customerId: this.order.id,
      status:     'TRIP_GENERATED',
      vendorId:   this.order.vendorId,
    };

    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to create trip' });
      }
    });
  }

  getOrderTripById(orderId: any): void {
    this.orderService.getOrderTripById(orderId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const data = res?.responseObject;
        if (data) {
          this.mapResponseToTrip(data);
          this.getEwayBillList(data?.id)
          this.getLrInfoList(data?.id)
        } else {
          this.ensureAtLeastOneLhcRow();
          this.ensureAtLeastOneLrRow();
          this.ensureAtLeastOneEwayRow();
          this.captureLoadingFormSnapshot();
        }
      },
      error: () => {
        this.isLoading = false;
        this.ensureAtLeastOneLhcRow();
        this.ensureAtLeastOneLrRow();
        this.ensureAtLeastOneEwayRow();
        this.captureLoadingFormSnapshot();
      }
    });
  }

  getEwayBillList(id: any) {

    const filter = [
      { key: 'TRIP_ID_IN', value: id }
    ];
  
    this.orderService.getEwayBillList(filter, 0, 100000).subscribe({
      next: (res: any) => {

        this.ewayBillList = res?.responseObject?.content || [];

        if (this.ewayBillList.length) {

          this.ewayRows = this.ewayBillList.map((item: any) => ({
            id: item.id,

            gstNumber: item.gstNumber,
            ewayBillNumber: item.ewbNo,

            fetched: true,

            invoiceNo: item.invoiceNo,
            invoiceValue: item.invValue,
            vehicleNo: item.vehicleNo,
            lrNumber: item.lrNo,

            rawInvoiceDate: item.invoiceDate,
            rawLrDate: item.lrDate,
            rawEwbDate: item.ewbDate,
            rawValidUpto: item.validUpto,

            invoiceDateObj: this.parseApiDateString(item.invoiceDate),
            lrDateObj: this.parseApiDateString(item.lrDate),
            ewbDateObj: this.parseApiDateString(item.ewbDate),
            validUptoObj: this.parseApiDateString(item.validUpto),

            docUrl: item.docUrl
          }));

        } else {
          // No saved E-Way Bills yet — always leave (or fall back to)
          // one blank form so the section is never empty by default.
          this.ensureAtLeastOneEwayRow();
        }
      },
      error: () => {
        // Fetch failed (e.g. nothing exists yet for a brand-new trip) —
        // fall back to a blank form rather than showing nothing.
        this.ensureAtLeastOneEwayRow();
      }
    });
  }
  getLrInfoList(id: any) {
    const filter = [
      { key: 'TRIP_ID_IN', value: id }
    ];
  
    this.orderService.getLrInfoList(filter, 0, 100000).subscribe({
      next: (res: any) => {

        this.lrInfoList = res?.responseObject?.content || [];

        if (this.lrInfoList.length) {

          this.lrRows = this.lrInfoList.map((item: any) => ({
            id: item.id,
            isManual: item.isManual,
            lrNumber: item.lrNumber || '',
            lrDate: this.toLocalDatetime(item.lrDate),
            remarks: item.remarks || '',
            lrDocument: item.lrDocument || item.docUrl || ''
          }));

        } else {
          // No saved LRs yet — always leave (or fall back to) one
          // blank form so the section is never empty by default.
          this.ensureAtLeastOneLrRow();
        }
      },
      error: () => {
        // Fetch failed (e.g. nothing exists yet for a brand-new trip) —
        // fall back to a blank form rather than showing nothing.
        this.ensureAtLeastOneLrRow();
      }
    });
  }

  // GST numbers for this account — feeds the e-Way Bill "GST Number" dropdown.
  loadGstList(): void {
    const accountId = this.order?.accountId || 1;
    this.orderService.getGstList(accountId).subscribe({
      next: (res: any) => {
        this.gstList = res?.responseObject || res?.data || [];
      },
      error: () => { this.gstList = []; }
    });
  }

  mapResponseToOrder(data: any): void {
    const pickup = data.orderPoints?.find((p: any) => p.pointType === 'PICKUP');
    const drop   = data.orderPoints?.find((p: any) => p.pointType === 'DROP');
    const route  = (data.source && data.destination)
      ? `${data.source} - ${data.destination}`
      : (pickup && drop)
        ? `${pickup.address} - ${drop.address}`
        : '';

    this.order = {
      id:                data.id,
      orderNo:           data.orderNumber,
      customerId:        data?.customerId,
      orderId:            data.orderId,
      customerName:      data.customerName || '',
      orderType:         data.orderType || 'spot',
      vehicleRequiredAt: data.vehicleRequiredAt || '',
      assignedKam:        data?.assignedKamId,
      assignedProcExec:       data?.assignedProcurementId,
      route,
      source:            data.source || pickup?.address || '',
      destination:       data.destination || drop?.address || '',
      vehicleTypeId:     data.vehicleTypeId,
      numberOfVehicle:   data.numberOfVehicle || 1,
      vendorId:          data.vendorId,
      accountId:         data.accountId,
      status:            data.status || '',
      confirmedAmount:   data.confirmedAmount,
      procurementProvidedAmount: data.procurementProvidedAmount,
      orderStatus:       data.orderStatus || [],
    };
  }

  get canStartTrip(): boolean {

    // LHC
    const hasLchDetail =
      Array.isArray(this.lhcRows) &&
      this.lhcRows.some(row =>
        row?.vehicleNumber?.toString().trim() &&
        row?.driverName?.toString().trim() &&
        row?.driverMobile?.toString().trim()
      );
  
    // LR
    const hasLR =
      Array.isArray(this.lrRows) &&
      this.lrRows.some(row =>
        row?.lrNumber?.toString().trim() &&
        row?.lrDate?.toString().trim()
      );
  
    // Reporting Date/Time
    const hasLoadingInfo =
      !!this.loadingForm?.reportingDateTime &&
      this.loadingForm.reportingDateTime.toString().trim() !== '';
  
    // E-Way Bill
    const hasEWayBill =
      Array.isArray(this.ewayRows) &&
      this.ewayRows.some(row =>
        row?.ewayBillNumber?.toString().trim()
      );

    const hasPickupRows = Array.isArray(this.pickupRows) &&
    this.pickupRows.some(row =>
      row?.pincode?.toString().trim() &&
      row?.city?.toString().trim() && 
      row?.address?.toString().trim() && 
      row?.lrId?.toString().trim() 
    );
  
    return (
      hasLchDetail &&
      hasLR &&
      hasLoadingInfo &&
      hasPickupRows &&
      hasEWayBill
    );
  }
  
  checkTripStatus(status:any): boolean {
    return this.order?.orderStatus?.some(
      (el: any) =>
        el.statusType === 'TRIP_STATUS' &&
        el.status === status
    ) || false;
  }

  // Trip API returns a single flat trip object — seed the first
  // LHC row and the loading-info form from it. LR / E-Way Bill
  // rows are independent forms the user builds up locally.
  mapResponseToTrip(trip: TripDataModel): void {
    this.tripData = trip;
    this.trackingData = trip.trackingId ? { url: trip.trackingId } : null;
    this.lhcRows = [{
      id:            trip.id,
      vehicleNumber: trip.vehicleNumber  || '',
      driverName:    trip.driverName     || '',
      driverMobile:  trip.driverMobile   || '',
      vendorMobile:  trip.vendorMobile   || '',
      vendorName:    trip.vendorName     || '',
      licenseNumber: trip.licenseNumber  || '',
      advanceAmount: trip.advanceAmount  ?? null,
      advancePercentage: trip.advancePercentage ?? null
    }];

    this.loadingForm = {
      reportingDateTime:       this.toLocalDatetime(trip.reportingDateTime),
      loadingDateTime:         this.toLocalDatetime(trip.loadingDateTime),
      sealNumber:              trip.sealNumber              || '',
      materialTypeDescription: trip.materialTypeDescription || '',
      weight:                  trip.weight          ?? null,
      numberOfPackages:        trip.numberOfPackages ?? null,
    };
    if(this.lhcRows[0].advanceAmount){
      this.onAdvanceAmountInput(this.lhcRows[0].advanceAmount,this.lhcRows[0])
    }
    else{
      this.onAdvancePercentageChange(this.lhcRows[0])
    }
    // Refresh the "last saved" snapshot so the Save button disables
    // itself again right after a successful save/reload.
    this.captureLoadingFormSnapshot();

    // Show one blank LR / E-Way Bill form immediately. If saved
    // records exist they'll replace these once the list fetches
    // below resolve; if nothing exists (or the fetch fails), the
    // section never renders empty — it always has a default form.
    this.ensureAtLeastOneLrRow();
    this.ensureAtLeastOneEwayRow();
  }

  ensureAtLeastOneLhcRow(): void {
    if (!this.lhcRows.length) {
      this.lhcRows = [this.emptyLhcRow()];
    }
  }

  ensureAtLeastOneLrRow(): void {
    if (!this.lrRows.length) {
      this.lrRows = [this.emptyLrRow()];
    }
  }

  ensureAtLeastOneEwayRow(): void {
    if (!this.ewayRows.length) {
      this.ewayRows = [this.emptyEwayRow()];
    }
  }

  emptyLhcRow(): LhcFormRow {
    return {
      vehicleNumber: '',
      driverName: '',
      driverMobile: '',
      vendorMobile: '',
      vendorName: '',
      licenseNumber: '',
      advanceAmount: null,
      advancePercentage: null
    };
  }

  emptyLrRow(): LrFormRow {
    return {
      isManual: true,
      lrNumber: '',
      lrDate: '',
      remarks: '',
    };
  }

  emptyEwayRow(): EwayBillRow {
    return {
      gstNumber: '',
      ewayBillNumber: '',
    };
  }

  // ─────────────────────────────────────────────
  // ROLE GATES
  // ─────────────────────────────────────────────

  canEditLocations(): boolean {
    return this.hasRole('ROLE_OPERATION');
  }

  canEditLhc(): boolean {
    return this.hasRole('ROLE_PROCUREMENT');
  }

  canEditLoadingInfo(): boolean {
    return this.hasRole('ROLE_OPERATION');
  }

  canEditLRInfo(): boolean {
    return this.hasRole('ROLE_OPERATION');
  }

  canEditEwayBill(): boolean {
    return this.hasRole('ROLE_OPERATION');
  }
  
  getVehicleType(){
    this.emsServices.getVehicleType([],0,100000).subscribe((res:any)=>{
      this.vehicleTypeList = res?.responseObject?.content;
    })
  }

  // ─────────────────────────────────────────────
  // Pickup / Drop Locations — repeatable PAIRS, single Save (ROLE_OPERATION)
  // Pickup & Drop are always added/removed together — 1 LR maps to
  // 1 pickup + 1 drop pair.
  // ─────────────────────────────────────────────

  emptyLocationRow(): LocationFormRow {
    return { pincode: '', city: null, otherCity: '', address: '', lrId: null };
  }

  ensureAtLeastOneLocationRow(): void {
    if (!this.pickupRows.length) this.pickupRows = [this.emptyLocationRow()];
    if (!this.dropRows.length) this.dropRows = [this.emptyLocationRow()];
  }

  // Adds one Pickup row + one Drop row together, as a pair.
  addLocationRow(): void {
    this.pickupRows.push(this.emptyLocationRow());
    this.dropRows.push(this.emptyLocationRow());
  }

  // Removes the Pickup + Drop row at the same index together, as a pair.
  removeLocationRow(index: number): void {
    if (this.pickupRows.length > 1) {
      this.pickupRows.splice(index, 1);
    }
    if (this.dropRows.length > 1) {
      this.dropRows.splice(index, 1);
    }
  }

  // Builds pickupRows / dropRows from the order's saved orderPoints.
  // Same PICKUP/DROP split as create-tms-order's patchLocationArray,
  // just against plain arrays instead of a FormArray.
  private populateLocationRows(orderPoints: any[]): void {
    const pickups = (orderPoints || []).filter((p: any) => p.pointType === 'PICKUP');
    const drops   = (orderPoints || []).filter((p: any) => p.pointType === 'DROP');

    this.pickupRows = pickups.map((p: any) => ({
      id: p.id,
      pincode: p.pincode || '',
      city: p.locationId ?? null,
      otherCity: '',
      address: p.address || '',
      lrId: p.lrId ?? null
    }));

    this.dropRows = drops.map((p: any) => ({
      id: p.id,
      pincode: p.pincode || '',
      city: p.locationId ?? null,
      otherCity: '',
      address: p.address || '',
      lrId: p.lrId ?? null
    }));

    this.ensureAtLeastOneLocationRow();
  }

  // A row only counts as "filled" if the user actually entered something.
  private isLocationRowFilled(loc: LocationFormRow): boolean {
    return !!(
      (loc.pincode && loc.pincode.toString().trim()) ||
      (loc.city !== null && loc.city !== undefined) ||
      (loc.otherCity && loc.otherCity.toString().trim()) ||
      (loc.address && loc.address.toString().trim())
    );
  }

  // All of Pincode / Location / Full Address are mandatory for every
  // pickup & drop row. (Select LR is shown on the UI too, but isn't
  // enforced here yet — see the note on LocationFormRow.lrNumber.)
  isLocationRowValid(loc: LocationFormRow): boolean {
    const hasLocation = loc.city === -1
      ? !!(loc.otherCity && loc.otherCity.toString().trim())
      : (loc.city !== null && loc.city !== undefined);

    return !!(
      loc.pincode && loc.pincode.toString().trim() &&
      hasLocation &&
      loc.address && loc.address.toString().trim()
      // && loc.lrNumber // TODO: enforce Select LR as mandatory once backend linking API is ready
    );
  }

  isLocationsValid(): boolean {
    return this.pickupRows.every(r => this.isLocationRowValid(r)) &&
           this.dropRows.every(r => this.isLocationRowValid(r));
  }

  private buildLocationPoints(pointType: 'PICKUP' | 'DROP', rows: LocationFormRow[]): any[] {
    return (rows || [])
      .filter((r: LocationFormRow) => this.isLocationRowFilled(r))
      .map((r: LocationFormRow) => ({
        ...(r.id ? { id: r.id } : {}),
        pointType,
        address: r.address || '',
        locationId: r.city === -1 ? null : r.city,
        pincode: r.pincode || '',
        lrId: r.lrId
        // lrNumber: r.lrNumber || undefined, // TODO: uncomment once backend supports linking an LR to a pickup/drop pair
      }));
  }

  saveLocations(): void {
    if (!this.isLocationsValid()) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Pincode, Location and Full Address are required for every pickup & drop' });
      return;
    }

    const orderPoints = [
      ...this.buildLocationPoints('PICKUP', this.pickupRows),
      ...this.buildLocationPoints('DROP', this.dropRows)
    ];

    if (!orderPoints.length) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Add at least one pickup or drop location' });
      return;
    }

    const payload: any = {
      id: this.order.id,
      orderPoints
    };

    this.locationsSaving = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: () => {
        this.locationsSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Pickup & drop locations saved successfully' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.locationsSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save locations' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // LHC — repeatable rows (ROLE_PROCUREMENT) — unchanged, per-row save
  // ─────────────────────────────────────────────

  addLhcRow(): void {
    this.lhcRows.push(this.emptyLhcRow());
  }

  removeLhcRow(index: number): void {
    if (this.lhcRows.length > 1) {
      this.lhcRows.splice(index, 1);
    }
  }

  isLhcRowValid(row: LhcFormRow): boolean {
    const totalFreight = Number(this.order?.procurementProvidedAmount || 0);
  
    return !!row.advanceAmount &&
           row.advanceAmount > 0 &&
           row.advanceAmount <= totalFreight;
  }

  saveLhcRow(row: LhcFormRow): void {
    if (!this.isLhcRowValid(row)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Advance Amount is required'
      });
      return;
    }

    const payload: any = {
      ...(row.id ? { id: row.id } : {}),
      orderId:          this.order.id,
      accountId:        this.tripData?.accountId || this.order.accountId || 1,
      customerId:       this.tripData?.customerId || this.order.id,
      vehicleNumber:    row.vehicleNumber,
      driverName:       row.driverName,
      driverMobile:     row.driverMobile,
      vendorMobile:     row.vendorMobile,
      vendorName:       row.vendorName,
      licenseNumber:    row.licenseNumber || undefined,
      advanceAmount:    row.advanceAmount?.toString() || '0',
      lhcStatus:        'CREATED',
      status:           'TRIP_GENERATED',
    };

    row.saving = true;
    this.orderService.saveTrip(payload).subscribe({
      next: (res: any) => {
        row.saving = false;
        row.id = res?.responseObject?.id || row.id;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'LHC details send to vendor for approval' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        row.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save LHC details' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // LR — Lorry Receipt — repeatable rows, single Save for all rows
  // ─────────────────────────────────────────────

  addLrRow(): void {
    this.lrRows.push(this.emptyLrRow());
  }

  removeLrRow(index: number): void {

    const row = this.lrRows[index];
    // Existing row
    if (row.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to delete LR'
      })
    }
  
    else {
      this.lrRows.splice(index, 1);
      if (!this.lrRows.length) {
        this.lrRows.push(this.emptyLrRow());
      }
    }
  }

  onLrTypeChange(row: LrFormRow): void {
    if (!row.isManual) {
      this.fetchGeneratedLrNumber(row);
    } else {
      row.lrNumber = '';
    }
  }
  
  fetchGeneratedLrNumber(row: LrFormRow): void {
    row.lrNumberGenerating = true;
    this.orderService.generateLrNumber().subscribe({
      next: (res: any) => {
        row.lrNumberGenerating = false;
        const generated = res?.responseObject?.lrNumber
          || res?.responseObject
          || res?.lrNumber
          || '';
        row.lrNumber = generated;
      },
      error: (err: any) => {
        row.lrNumberGenerating = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to generate LR number' });
      }
    });
  }

  onLrFileSelected(event: any, row: LrFormRow): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    row.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => {
        row.uploading = false;
        row.lrDocument = url;
      },
      error: () => {
        row.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload LR document' });
      }
    });
  }

  isLrRowValid(row: LrFormRow): boolean {
    return !!(row.lrNumber && row.lrDate);
  }

  // Single Save for the whole LR section — fires one addOrUpdate
  // call per row, in parallel, and reports success only once all
  // rows have saved.
  saveAllLr(): void {
    const invalidRow = this.lrRows.find(r => !this.isLrRowValid(r));
    if (invalidRow) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'LR number & LR date are required for every row' });
      return;
    }

    const newRows = this.lrRows.filter(x => !x.id);
    if (!newRows.length) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'There are no new LR records to save.'
      });
      return;
    }
    const requests = newRows.map(row => {
      const payload: any = {
        orderId:     this.orderId,
        tripId:      this.tripData?.id,
        isManual:    row.isManual,
        lrNumber:    row.lrNumber,
        lrDate:      this.toApiDatetime(row.lrDate),
        lrDocument:  row.lrDocument || undefined,
        remarks:     row.remarks || undefined,
      };
      return this.orderService.saveAllLr(payload);
    });

    this.lrSaving = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.lrSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'LR details saved successfully' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.lrSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save LR details' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // Loading Info (ROLE_OPERATION)
  // ─────────────────────────────────────────────

  // True when the current loadingForm differs from the last
  // saved/loaded snapshot — mirrors hasNewLrRows()/hasNewEwayRows()
  // so the Save button behaves consistently across sections.
  hasLoadingInfoChanges(): boolean {
    return JSON.stringify(this.loadingForm) !== this.loadingFormSnapshot;
  }

  private captureLoadingFormSnapshot(): void {
    this.loadingFormSnapshot = JSON.stringify(this.loadingForm);
  }

  saveLoadingInfo(): void {
    if (!this.hasLoadingInfoChanges()) {
      return;
    }

    const payload: any = {
      ...(this.tripData?.id ? { id: this.tripData.id } : {}),
      orderId:                 this.order.id,
      accountId:               this.tripData?.accountId || this.order.accountId || 1,
      customerId:              this.tripData?.customerId || this.order.id,
      reportingDateTime:       this.toApiDatetime(this.loadingForm.reportingDateTime),
      loadingDateTime:         this.toApiDatetime(this.loadingForm.loadingDateTime),
      sealNumber:              this.loadingForm.sealNumber              || undefined,
      materialTypeDescription: this.loadingForm.materialTypeDescription || undefined,
      weight:                  this.loadingForm.weight                  ?? undefined,
      numberOfPackages:        this.loadingForm.numberOfPackages        ?? undefined,
    };

    this.loadingSaving = true;
    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.loadingSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Loading info saved successfully' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.loadingSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save loading info' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // E-Way Bill — repeatable rows, single Save for all rows
  // ─────────────────────────────────────────────

  addEwayRow(): void {
    this.ewayRows.push(this.emptyEwayRow());
  }

  removeEwayRow(index: number): void {
    const row = this.ewayRows[index];
    if (row.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to delete E-Way Bill'
      })
  
    } else {
      this.ewayRows.splice(index, 1);
      if (!this.ewayRows.length) {
        this.ewayRows.push(this.emptyEwayRow());
      }
    }  
  }

  gstValue(g: any): string {
    return g?.gstNumber || g?.gstNo || g?.gstin || (typeof g === 'string' ? g : '');
  }

  gstLabel(g: any): string {
    const num = this.gstValue(g);
    const name = g?.tradeName || g?.legalName || g?.name;
    return name ? `${num} — ${name}` : num;
  }

  // Pulls invoice / vehicle / validity details for one e-Way Bill row
  // from the e-Way bill lookup. Date fields come back pre-formatted
  // as "DD-MM-YYYY HH:mm:ss +0000" — the same format the save API
  // expects, so they're stored as-is and only parsed separately for
  // on-screen display.
  fetchEwayBill(row: EwayBillRow): void {
    if (!row.gstNumber || !row.ewayBillNumber) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select a GST number and enter the e-Way bill number' });
      return;
    }
    row.fetching = true;
    row.errorMessage = undefined;
    this.orderService.fetchEwayBill(row.ewayBillNumber,row.gstNumber).subscribe({
      next: (res: any) => {
        row.fetching = false;
        row.fetched = true;
        row.errorMessage = undefined;
        const data = res?.responseObject || {};

        row.ewbId        = data.ewbId;
        row.invoiceNo    = data.invoiceNo;
        row.invoiceValue = data.invValue;
        row.vehicleNo    = data.vehicleNo;
        row.lrNumber     = data.lrNo;

        row.rawInvoiceDate = data.invoiceDate;
        row.rawLrDate      = data.lrDate;
        row.rawEwbDate     = data.ewbDate;
        row.rawValidUpto   = data.validUpto;

        row.invoiceDateObj = this.parseApiDateString(data.invoiceDate);
        row.lrDateObj      = this.parseApiDateString(data.lrDate);
        row.ewbDateObj      = this.parseApiDateString(data.ewbDate);
        row.validUptoObj   = this.parseApiDateString(data.validUpto);
      },
      error: (err: any) => {
        row.fetching = false;
        row.fetched = false;
        row.errorMessage = err?.error?.responseMessage || 'Failed to fetch e-Way bill details';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to fetch e-Way bill details' });
      }
    });
  }

  onEwayFileSelected(event: any, row: EwayBillRow): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    row.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => {
        row.uploading = false;
        row.docUrl = url;
      },
      error: () => {
        row.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload e-Way bill document' });
      }
    });
  }

  hasNewLrRows(): boolean {
    return this.lrRows.some(x => !x.id);
  }

  hasNewEwayRows(): boolean {
    return this.ewayRows.some(x => !x.id && x.fetched);
  }

  // Single Save for the whole E-Way Bill section — every row must
  // have been fetched first, then all rows save in parallel.
  saveAllEway(): void {
    const newRows = this.ewayRows.filter(x => !x.id && x.fetched);
    if (!newRows.length) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'There are no new E-Way Bills to save.'
      });
      return;
    }
    const notFetched = newRows.find(r => !r.fetched);
    if (notFetched) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Fetch e-Way bill details for every row before saving' });
      return;
    }

    const requests = newRows.map(row => {
      const payload: any = {
        orderId:     this.order.id,
        tripId:      this.tripData?.id,
        accountId:   this.order.accountId || 1,
        ewbNo:       row.ewayBillNumber,
        lrNo:        row.lrNumber,
        lrDate:      row.rawLrDate,
        invoiceNo:   row.invoiceNo,
        invoiceDate: row.rawInvoiceDate,
        invValue:    row.invoiceValue,
        vehicleNo:   row.vehicleNo,
        ewbDate:     row.rawEwbDate,
        validUpto:   row.rawValidUpto,
        gstNumber:       row.gstNumber,
        docUrl:      row.docUrl || undefined,
      };
      return this.orderService.saveEwayBill(payload);
    });

    this.ewaySaving = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.ewaySaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'E-Way bill(s) saved successfully' });
      },
      error: (err: any) => {
        this.ewaySaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save e-Way bill(s)' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FILE UPLOAD
  // NOTE: form field name ('file') and the response shape used to
  // pull the URL back out are best-effort guesses — confirm against
  // the real utils-service contract and adjust if needed.
  // ─────────────────────────────────────────────

  uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    this.uploadApi = this.orderService.uploadFilesURL()
    return this.http.post(this.uploadApi, formData).pipe(
      map((res: any) =>
        res?.responseObject?.url
        || res?.responseObject?.fileUrl
        || (typeof res?.responseObject === 'string' ? res.responseObject : '')
        || res?.url
        || res?.fileUrl
        || ''
      )
    );
  }

  // ─────────────────────────────────────────────
  // STATUS HELPERS
  // ─────────────────────────────────────────────

  statusLabel(): string {
    if (!this.order?.status) return '--';
    return this.order.status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  statusBadgeClass(): string {
    const s = this.order?.status?.toUpperCase();
    if (!s) return 'badge-grey';
    if (s.includes('CONFIRMED') || s.includes('APPROVED')) return 'badge-green';
    if (s.includes('PENDING') || s.includes('GENERATED') || s.includes('INPROGRESS')) return 'badge-yellow';
    if (s.includes('REJECTED')) return 'badge-red';
    return 'badge-grey';
  }

  tripStatusLabel(): string {
    const tripStatus = this.order?.orderStatus?.find(s => s.statusType === 'TRIP_STATUS');
    if (!tripStatus) return '--';
    return this.humanize(tripStatus.status);
  }

  onPickupLrChange(index: number): void {
    if (this.dropRows[index]) {
      this.dropRows[index].lrId = this.pickupRows[index].lrId;
    }
  }
  
  onDropLrChange(index: number): void {
    if (this.pickupRows[index]) {
      this.pickupRows[index].lrId = this.dropRows[index].lrId;
    }
  }

  // Raw (non-humanized) TRIP_STATUS value — used to pick the pill
  // color, since statusPillClass() matches on the raw status text.
  rawTripStatus(): string {
    const tripStatus = this.order?.orderStatus?.find(s => s.statusType === 'TRIP_STATUS');
    return tripStatus?.status || '';
  }

  trackingStatus(): string {
    const tripStatus = this.order?.orderStatus?.find(s => s.statusType === 'TRIP_STATUS');
    return tripStatus?.status === 'TRIP_INPROGRESS' || tripStatus?.status === 'TRIP_STARTED'
      ? 'ACTIVE'
      : 'NOT STARTED';
  }

  humanize(value: string | undefined | null): string {
    if (!value) return '--';
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  formatStatusType(value: string | undefined | null): string {
    if (!value) return '--';
  
    let text = value.replace(/_STATUS$/i, '');
  
    if (text.toUpperCase() === 'ADVANCE_PAYMENT') {
      return 'Advance';
    }
  
    return text
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/\bLhc\b/g, 'LHC')
      .replace(/\bPod\b/g, 'POD');
  }

  getRoles() {
    this.emsServices.getRoles().subscribe((res: any) => {
      this.allRoles = res?.responseObject;
      this.allRoles.forEach((el: any) => {
        if (el.name === 'ROLE_KAM') {
          this.getKam(el.id);
        }
        if (el.name === 'ROLE_PROCUREMENT') {
          this.getProcurement(el.id);
        }
      });
    });
  }

  getKam(id: any) {
    const postData = [{ key: 'ROLE_ID_IN', value: id.toString() }];
    this.emsServices.getUsers(postData, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.kamList = res?.responseObject?.content;
    });
  }

  getProcurement(id: any) {
    const postData = [{ key: 'ROLE_ID_IN', value: id.toString() }];
    this.emsServices.getUsers(postData, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.procurementExecList = res?.responseObject?.content;
    });
  }

  // Maps any of the small per-item statuses (TRIP_STATUS, LHC_STATUS,
  // SOFT_POD_STATUS, CLAIM STATUS, etc.) to one of the same badge
  // colors used everywhere else in the app, so every pill — header,
  // timeline cards, and General Details — reads consistently.
  statusPillClass(value: string | undefined | null): string {
    const v = (value || '').toUpperCase().trim();
    if (!v || v === 'NA' || v === 'N/A' || v === '--') return 'badge-grey';
    if (v.includes('PENDING') || v.includes('INPROGRESS') || v.includes('IN_PROGRESS')) return 'badge-yellow';
    if (v.includes('REJECTED') || v.includes('CANCELLED') || v.includes('FAILED')) return 'badge-red';
    if (
      v.includes('GENERATED') ||
      v.includes('CONFIRMED') ||
      v.includes('APPROVED') ||
      v.includes('COMPLETED') ||
      v.includes('DONE') ||
      v.includes('PAID') ||
      v.includes('STARTED') ||
      v.includes('ENDED')
    ) return 'badge-green';
    return 'badge-grey';
  }

  // Companion dot color for the Order Status timeline — mirrors
  // statusPillClass() rather than the old "PENDING vs everything
  // else is done" assumption (which mis-colored rejected/NA items).
  statusDotClass(value: string | undefined | null): string {
    const cls = this.statusPillClass(value);
    if (cls === 'badge-yellow') return 'dot-pending';
    if (cls === 'badge-red') return 'dot-red';
    if (cls === 'badge-green') return 'dot-done';
    return 'dot-grey';
  }

  // ─────────────────────────────────────────────
  // COMPUTED
  // ─────────────────────────────────────────────

  balancePayable(row: LhcFormRow): number {
    const freight = this.order?.procurementProvidedAmount || 0;
    const advance = row.advanceAmount || 0;
    return freight - advance;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '--';
    return '₹' + Number(value).toLocaleString('en-IN');
  }

  onAdvanceAmountInput(value: any, row: any): void {
    const totalFreight = Number(this.order?.procurementProvidedAmount || 0);
  
    if (!value) {
      row.advanceAmount = null;
      row.advancePercentage = null;
      return;
    }
  
    if (value > totalFreight) {
      // row.advanceAmount = totalFreight;
    } else {
      row.advanceAmount = value;
    }
  
    row.advancePercentage = Number(
      ((row.advanceAmount / totalFreight) * 100).toFixed(2)
    );
  }
  
  onAdvancePercentageChange(row: any): void {
    const totalFreight = Number(this.order?.procurementProvidedAmount || 0);
  
    let percentage = Number(row.advancePercentage || 0);
  
    // Restrict between 0 and 100
    if (percentage < 0) {
      percentage = 0;
    }
  
    if (percentage > 100) {
      percentage = 100;
    }
  
    row.advancePercentage = percentage;
  
    row.advanceAmount = Number(
      ((totalFreight * percentage) / 100).toFixed(2)
    );
  }

  formatDisplayDate(d: Date | null | undefined): string {
    if (!d) return '--';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // Parses "17-06-2026 04:17:00 +0000" → Date, for display formatting only.
  // The raw string itself (not this parsed Date) is what gets sent back
  // to the save API, since it's already in the exact format expected.
  parseApiDateString(raw: string | undefined): Date | null {
    if (!raw) return null;
    const match = raw.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})/);
    if (!match) return null;
    const [, dd, mm, yyyy, hh, min, ss] = match;
    return new Date(Date.UTC(+yyyy, +mm - 1, +dd, +hh, +min, +ss));
  }

  // Convert API date "29-06-2026 23:42:00 +0530" → "2026-06-29T23:42"
  toLocalDatetime(apiDate: string | undefined): string {
    if (!apiDate) return '';
    const match = apiDate.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})/);
    if (!match) return '';
    const [, dd, mm, yyyy, hh, min] = match;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  // Convert "2026-06-29T23:42" → "29-06-2026 23:42:00 +0530"
  // Used by LHC, Loading Info, and LR — all on the tms-service/trip API.
  toApiDatetime(localDt: string): string {
    if (!localDt) return '';
    const [datePart, timePart] = localDt.split('T');
    const [yyyy, mm, dd] = datePart.split('-');
    return `${dd}-${mm}-${yyyy} ${timePart}:00 +0530`;
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  cancelTrip(): void {
    const payload: any = {
      id:         this.tripData?.id,
      orderId:    this.tripData?.orderId,
      accountId:  this.tripData?.accountId,
      customerId: this.tripData?.customerId,
      status:     'CANCELED',
    };

    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
        this.getOrderDatabyId(this.orderId);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Trip canceled successfully started' });
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to cancel trip' });
      }
    });
  }

  startTrip(): void {
    const payload: any = {
      id:         this.tripData?.id,
      orderId:    this.tripData?.orderId,
      accountId:  this.tripData?.accountId,
      customerId: this.tripData?.customerId,
      status:     'TRIP_INPROGRESS',
    };

    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
        this.getOrderDatabyId(this.orderId);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Trip successfully started' });
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to start trip' });
      }
    });
  }
  
  canFetchTracking(): boolean {
    const row = this.lhcRows?.[0];
    return !!(
      (this.tripData?.vehicleNumber || row?.vehicleNumber) &&
      (this.tripData?.driverName    || row?.driverName) &&
      (this.tripData?.driverMobile  || row?.driverMobile)
    );
  }
  
  saveTracking(url:any): void {
    const payload: any = {
      ...(this.tripData?.id ? { id: this.tripData.id } : {}),
      orderId:                 this.order.id,
      accountId:               this.tripData?.accountId || this.order.accountId || 1,
      customerId:              this.tripData?.customerId || this.order.id,
      trackingId:              url ?? ''
    };

    this.loadingSaving = true;
    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
      }
    });
  }
  
  fetchTracking(): void {
    if (!this.canFetchTracking() || this.trackingFetching) return;
  
    const row = this.lhcRows?.[0];
    const payload: any = {
      id:            this.tripData?.id,
      orderId:       this.order?.id,
      tripNumber:    this.tripData?.tripNumber,
      vehicleNumber: this.tripData?.vehicleNumber || row?.vehicleNumber,
      driverMobile:  this.tripData?.driverMobile  || row?.driverMobile,
      driverName:    this.tripData?.driverName    || row?.driverName,
    };
  
    this.trackingFetching = true;
    this.orderService.fetchTracking(payload).subscribe({
      next: (res: any) => {
        this.trackingFetching = false;
  
        if (!res?.responseStatus) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: res?.responseMessage || 'Failed to fetch tracking' });
          return;
        }
  
        const result = res?.responseObject?.result || {};
        this.trackingData = {
          id:       result.id,
          url:      result.shareUrl,
          lrNumber: result.lr_number,
        };
  
        if (!this.trackingData.url) {
          this.messageService.add({ severity: 'warn', summary: 'No URL', detail: 'Tracking fetched but no URL was returned' });
        } else {
          this.saveTracking(this.trackingData.url)
          this.messageService.add({ severity: 'success', summary: 'Success', detail: res?.responseObject?.message || 'Tracking fetched successfully' });
        }
      },
      error: (err: any) => {
        this.trackingFetching = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to fetch tracking' });
      }
    });
  }

  acceptRejectManagement(status:any){
    const payload: any = {
      id:         this.tripData?.id,
      orderId:    this.tripData?.orderId,
      accountId:  this.tripData?.accountId,
      customerId: this.tripData?.customerId,
      managementStatus: status
    };

    this.orderService.tripGenration(payload).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.responseMessage || `LHC ${status === 'APPROVED' ? 'approved' : status === 'SUBMIT' ? 'submit' : 'rejected'} successfully`
        });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Something went wrong'
        });
      }
    });
  }

  downloadEwayTemplate(): void {
    const ws = XLSX.utils.aoa_to_sheet([['GST Number', 'E-Way Bill Number']]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'EwayBills');
    XLSX.writeFile(wb, 'eway-bill-template.xlsx');
  }

  openEwayBulkModal(): void {
    this.ewayBulkRows = [];
    this.showEwayBulkModal = true;
  }
  
  closeEwayBulkModal(): void {
    if (this.ewayBulkProcessing) return; // don't let them close mid-run
    this.showEwayBulkModal = false;
  }

  onEwayBulkDialogHide(): void {
    // p-dialog already closed itself visually; just guard against
    // closing mid-processing by re-opening if needed
    if (this.ewayBulkProcessing) {
      this.showEwayBulkModal = true;
    }
  }

  onEwayBulkFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
  
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  
      // Skip header row, drop blank rows
      this.ewayBulkRows = rows
        .slice(1)
        .filter(r => r && (r[0] || r[1]))
        .map(r => ({
          gstNumber: (r[0] ?? '').toString().trim(),
          ewayBillNumber: (r[1] ?? '').toString().trim(),
          status: 'pending' as const
        }))
        .filter(r => r.gstNumber && r.ewayBillNumber);
  
      // allow re-selecting the same file next time
      event.target.value = '';
  
      this.processEwayBulkRows();
    };
    reader.readAsArrayBuffer(file);
  }

  async processEwayBulkRows(): Promise<void> {
    this.ewayBulkProcessing = true;
  
    for (const bulkRow of this.ewayBulkRows) {
      const existing = this.ewayRows.find(
        r => r.gstNumber === bulkRow.gstNumber && r.ewayBillNumber === bulkRow.ewayBillNumber
      );
  
      // Already saved to backend — never re-fetch, never re-save.
      if (existing?.id) {
        bulkRow.status = 'skipped';
        bulkRow.errorMessage = 'Already saved';
        continue;
      }
  
      // Already fetched successfully in this session but not yet saved —
      // no need to hit the API again.
      if (existing?.fetched) {
        bulkRow.status = 'success';
        continue;
      }
  
      bulkRow.status = 'fetching';
      try {
        const detail = await this.fetchEwayBillDetail(bulkRow.gstNumber, bulkRow.ewayBillNumber);
        this.upsertEwayRowFromFetch(bulkRow.gstNumber, bulkRow.ewayBillNumber, detail);
        bulkRow.status = 'success';
      } catch (err: any) {
        const msg = err?.error?.responseMessage || 'Failed to fetch details';
        this.upsertEwayRowAsError(bulkRow.gstNumber, bulkRow.ewayBillNumber, msg);
        bulkRow.status = 'error';
        bulkRow.errorMessage = msg;
      }
    }
  
    this.ewayBulkProcessing = false;
  }
  
  private fetchEwayBillDetail(gstNumber: string, ewbNumber: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.orderService.fetchEwayBill(ewbNumber, gstNumber).subscribe({
        next: (res: any) => resolve(res?.responseObject || {}),
        error: (err: any) => reject(err)
      });
    });
  }

  private findOrCreateBulkTargetRow(gstNumber: string, ewbNumber: string): EwayBillRow {
    let row = this.ewayRows.find(
      r => !r.id && r.gstNumber === gstNumber && r.ewayBillNumber === ewbNumber
    );
    if (row) return row;
  
    row = this.emptyEwayRow();
    row.gstNumber = gstNumber;
    row.ewayBillNumber = ewbNumber;
  
    // Replace the lone untouched placeholder row instead of stacking a
    // blank card above the real ones.
    if (this.ewayRows.length === 1 && !this.ewayRows[0].gstNumber && !this.ewayRows[0].ewayBillNumber) {
      this.ewayRows[0] = row;
    } else {
      this.ewayRows.push(row);
    }
    return row;
  }
  
  private upsertEwayRowFromFetch(gstNumber: string, ewbNumber: string, data: any): void {
    const row = this.findOrCreateBulkTargetRow(gstNumber, ewbNumber);
  
    row.fetching = false;
    row.fetched = true;
    row.errorMessage = undefined;
  
    row.ewbId        = data.ewbId;
    row.invoiceNo    = data.invoiceNo;
    row.invoiceValue = data.invValue;
    row.vehicleNo    = data.vehicleNo;
    row.lrNumber     = data.lrNo;
  
    row.rawInvoiceDate = data.invoiceDate;
    row.rawLrDate      = data.lrDate;
    row.rawEwbDate     = data.ewbDate;
    row.rawValidUpto   = data.validUpto;
  
    row.invoiceDateObj = this.parseApiDateString(data.invoiceDate);
    row.lrDateObj      = this.parseApiDateString(data.lrDate);
    row.ewbDateObj     = this.parseApiDateString(data.ewbDate);
    row.validUptoObj   = this.parseApiDateString(data.validUpto);
  }
  
  private upsertEwayRowAsError(gstNumber: string, ewbNumber: string, message: string): void {
    const row = this.findOrCreateBulkTargetRow(gstNumber, ewbNumber);
    row.fetching = false;
    row.fetched = false;
    row.errorMessage = message;
  }

  canPrintLrTemplate(): boolean {
    const lhcSaved = this.lhcRows.length > 0 && this.lhcRows.every(r => !!r.id);
    const lrSaved = this.lrRows.length > 0 && !this.hasNewLrRows();
    const loadingSaved = !this.hasLoadingInfoChanges() && !!this.loadingForm?.reportingDateTime;
    const ewaySaved = this.ewayRows.length > 0 && !this.hasNewEwayRows();
    return lhcSaved && lrSaved && loadingSaved && ewaySaved;
  }
   
  private getVehicleTypeLabel(): string {
    const vt = this.vehicleTypeList?.find((v: any) => v.id === this.order?.vehicleTypeId);
    return vt?.vehicleType;
  }
   
  // Picks the e-Way bill that belongs to a given LR row — matched on
  // LR number when available, otherwise falls back to the first
  // fetched e-Way bill so the sheet isn't left completely blank.
  private matchEwayBillForLr = (lrRow: any) => {
    return (
      this.ewayRows.find(b => b.fetched && b.lrNumber && b.lrNumber === lrRow.lrNumber) ||
      this.ewayRows.find(b => b.fetched)
    );
  };
   
  // Picks the pickup/drop pair linked to a given LR row (Section 3d's
  // "Select LR" dropdown sets pickup.lrId / drop.lrId to lrRow.id).
  // Falls back to the only pickup/drop row when there's just one pair,
  // so single-LR trips still prefill pincodes even if the user never
  // touched the "Select LR" dropdown.
  private matchLocationsForLr = (lrRow: any): { pickupRow?: any; dropRow?: any } => {
    const pickupRow =
      this.pickupRows.find(p => p.lrId != null && p.lrId === lrRow.id) ||
      (this.pickupRows.length === 1 ? this.pickupRows[0] : undefined);
    const dropRow =
      this.dropRows.find(d => d.lrId != null && d.lrId === lrRow.id) ||
      (this.dropRows.length === 1 ? this.dropRows[0] : undefined);
    return { pickupRow, dropRow };
  };
   
  // Builds Consignor / Consignee / POD copies for every LR row in one
  // combined HTML document (one printed page per copy).
  private buildFilledHtml(): string {
    const dataList = buildLrDocumentData(
      this.order,
      this.lrRows,
      this.lhcRows[0],
      this.loadingForm,
      this.ewayRows,
      this.getVehicleTypeLabel(),
      undefined, // logoUrl — omit to use DEFAULT_LOGO_URL, or pass a per-account logo here
      this.matchEwayBillForLr,
      this.matchLocationsForLr
    );
   
    if (!dataList.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to print',
        detail: 'No saved LR found for this trip yet.'
      });
      return '';
    }
   
    return renderLrDocument(dataList);
  }
   
  printLrTemplate(): void {
    const html = this.buildFilledHtml();
    if (!html) return;
    printLrHtml(html);
  }
   
  downloadLrTemplate(): void {
    const html = this.buildFilledHtml();
    if (!html) return;
    downloadLrHtml(html, `LR-${this.order.orderNo}.html`);
  }
  
}